import starSrc from "@icons/star.svg?url";
import starFilledSrc from "@icons/star-filled.svg?url";
import starOutlineSrc from "@icons/star-outline.svg?url";
import { msg, t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import partition from "lodash-es/partition";
import { DateTime } from "luxon";
import { Navigate } from "react-router-dom";
import invariant from "tiny-invariant";

import { CropIcon } from "@/common/components/crop-icon/crop-icon";
import { Failed } from "@/common/components/info/info";
import { Metadata } from "@/common/components/metadata/metadata";
import { trackQuarterClick } from "@/common/components/mixpanel/mixpanel-tracking";
import { PageTitle } from "@/common/components/page-title/page-title";
import {
  PriceItem,
  PriceList,
} from "@/common/components/price-list/price-list";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spacer } from "@/common/components/spacer/spacer";
import { Spinner } from "@/common/components/spinner/spinner";
import { WrapMeWith } from "@/common/components/wrap-me-with/wrap-me-with";
import { useCurrency } from "@/common/hooks/use-currency";
import { useRequiredErpCompany } from "@/common/hooks/use-erp-company";
import { useLocalStorage } from "@/common/hooks/use-local-storage";
import { useScrollbarToggle } from "@/common/hooks/use-scrollbar-toggle";
import { useCompany } from "@/common/providers/company-provider";
import { oneMinute } from "@/common/providers/query-provider";
import { toggleItem } from "@/common/utils/array";
import { getPricesView } from "@/common/utils/trading";
import { Currency } from "@/generated/digitalnisklady.cz/graphql";

import { TwoColumnLayout } from "../layout/layout";
import {
  cropPricesQuery,
  selectBestCropPrices,
  selectCropPrices,
} from "../shared/api/crop-prices.api";
import {
  PostalCodeDialog,
  usePostalCodeDialogParams,
} from "../shared/components/postal-code-dialog/postal-code-dialog";
import { PostalCodeInfoBox } from "../shared/components/postal-code-dialog/postal-code-info-box";
import { usePostalCodeObject } from "../shared/components/postal-code-dialog/use-postal-code-object";
import { useIsOpen } from "./quick-trading.detail";

const path = "quick-trading";

const QuickTrading = () => {
  const { id: companyId } = useRequiredErpCompany();
  const isRightPaneOpen = useIsOpen();
  const { _ } = useLingui();
  const postalCodeDialog = usePostalCodeDialogParams();
  useScrollbarToggle(postalCodeDialog.params?.enforceDialog);
  const [CurrencySwitch, currency] = useCurrency();
  const postalCodeObject = usePostalCodeObject(
    postalCodeDialog.params?.postalCode,
  );
  const [favorites, setFavorites] = useFavorites();

  const cropPrices = useQuery({
    refetchInterval: oneMinute,
    ...cropPricesQuery({
      companyId,
      currency: currency as Currency,
      // @ts-expect-error this can hardly ever be undefined, yet I have no idea how to express it here
      latitude: postalCodeObject.latitude,
      // @ts-expect-error this can hardly ever be undefined, yet I have no idea how to express it here
      longitude: postalCodeObject.longitude,
    }),
    enabled: !!postalCodeObject.latitude,
    select: (data) => selectBestCropPrices(selectCropPrices(data)),
  });
  const [favoriteCropPrices, otherCropPrices] = partition(
    cropPrices.data,
    (price) => favorites.includes(price.crop.name),
  );

  const toggleFavorite = (cropName: string) => {
    setFavorites((current) => (current ? toggleItem(current, cropName) : []));
  };

  if (!postalCodeObject.valid) {
    return <Navigate to="?" />;
  }

  return (
    <TwoColumnLayout
      left={{
        content: (
          <div className="flex basis-full flex-col">
            <Metadata title={_(msg`Rychlý prodej komodit`)} />
            <PostalCodeInfoBox
              code={postalCodeObject.code}
              name={postalCodeObject.name}
              onPostalCodeChange={postalCodeDialog.enforceDialog}
            >
              <Trans>
                Nyní zobrazujeme aktuální ceny komodit pro oblast dle poštovního
                směrovacího čísla (PSČ)
              </Trans>
            </PostalCodeInfoBox>
            <Spacer className="h-6" />
            {cropPrices.status === "pending" ? (
              <div className="flex basis-full items-center justify-center">
                <Spinner />
              </div>
            ) : null}
            {cropPrices.status === "error" ? (
              <Failed error={cropPrices.error} />
            ) : null}
            {cropPrices.status === "success" ? (
              <div className="flex flex-col gap-y-8">
                {[
                  {
                    icon: starFilledSrc,
                    iconTitle: t`Odebrat z oblíbených`,
                    items: favoriteCropPrices,
                    title: (
                      <span
                        className="flex items-center gap-2"
                        title="Zaškrtnutím hvězdičky přidáte komoditu do oblíbených"
                      >
                        <img src={starOutlineSrc} />{" "}
                        <Trans>Moje oblíbené komodity</Trans>
                      </span>
                    ),
                    titleTooltip: t`Zaškrtnutím hvězdičky u jednotlivých komodit je přesunete v seznamu výše`,
                  },
                  {
                    icon: starSrc,
                    iconTitle: t`Přidat do oblíbených`,
                    items: otherCropPrices,
                    title: t`Ostatní komodity`,
                  },
                ].map((section) => (
                  <section
                    className="flex flex-col gap-4"
                    key={section.iconTitle}
                  >
                    <h2
                      className="text-can-forest-teal flex items-center gap-2 font-bold"
                      title={section.titleTooltip}
                    >
                      {section.title}
                    </h2>

                    <div
                      className={`grid w-full gap-6 ${isRightPaneOpen ? "grid-cols-1 2xl:grid-cols-2" : "grid-cols-1 xl:grid-cols-3"}`}
                    >
                      {section.items.map((item) => (
                        <section
                          className="shadow-can-light-box flex flex-col gap-y-2 rounded-2xl p-4"
                          key={item.crop.name}
                        >
                          <h2 className="text-can-midnight-steel flex items-center gap-x-2 font-bold">
                            <CropIcon cropName={item.crop.name} />{" "}
                            {item.crop.name}
                            <button
                              className="group hover:bg-can-silver-gray -mr-2 ml-auto flex content-center rounded-full p-2"
                              onClick={() => toggleFavorite(item.crop.name)}
                              title={section.iconTitle}
                            >
                              <img
                                className="group-hover:hidden"
                                src={section.icon}
                              />
                              <img
                                className="hidden group-hover:inline"
                                src={starOutlineSrc}
                              />
                            </button>
                          </h2>
                          {getPricesView(item.prices).map((prices, i) => (
                            <PriceList
                              className={
                                i === 0 && prices.length === 1
                                  ? "[&>li]:col-start-4 [&>li]:border-r-0 [&>li]:border-l"
                                  : ""
                              }
                              key={i}
                              prices={prices.map((p) =>
                                p?.type === "data"
                                  ? {
                                      currency,
                                      id: p.id,
                                      quarter: p.quarter,
                                      value: p.price,
                                    }
                                  : null,
                              )}
                              renderPrice={(price) =>
                                price ? (
                                  <WrapMeWith
                                    when={!!price}
                                    wrapper={(children) => (
                                      <SearchParamsLink
                                        className="text-can-slate-blue-gray flex flex-col gap-y-2"
                                        onMouseDown={() => {
                                          trackQuarterClick({
                                            crop: item.crop.name,
                                            currency: price.currency,
                                            isFavorite: favorites.includes(
                                              item.crop.name,
                                            ),
                                            price: price.value,
                                            quarter: price.quarter.name,
                                          });
                                        }}
                                        to={
                                          item.crop.id +
                                          "/quarter/" +
                                          price.quarter.id
                                        }
                                      >
                                        {children}
                                      </SearchParamsLink>
                                    )}
                                  >
                                    <PriceItem price={price} />
                                  </WrapMeWith>
                                ) : null
                              }
                            />
                          ))}
                        </section>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            ) : null}
            <Spacer />
            {postalCodeDialog.params?.enforceDialog ? (
              <PostalCodeDialog>
                <Trans>
                  Pro výpočet ceny vyplňte do pole níže poštovní směrovací číslo
                  umístění skladu (PSČ).
                </Trans>
              </PostalCodeDialog>
            ) : null}
            <Spacer />
            {cropPrices.dataUpdatedAt > 0 ? (
              <p className="mt-auto mb-8 text-center text-sm">
                <Trans>Aktualizováno</Trans>{" "}
                {DateTime.fromMillis(cropPrices.dataUpdatedAt).toLocaleString(
                  DateTime.DATETIME_FULL,
                )}
              </p>
            ) : null}
          </div>
        ),
        header: (
          <>
            <PageTitle>
              <Trans>Rychlý prodej komodit</Trans>
            </PageTitle>
            {CurrencySwitch}
          </>
        ),
      }}
      right={isRightPaneOpen}
    ></TwoColumnLayout>
  );
};

const useFavorites = () => {
  const { company: activeCompany } = useCompany();
  invariant(activeCompany);
  const [favorites, setFavorites] = useLocalStorage<string[]>(
    `purchase-prices.${activeCompany.id}.favorites`,
    [],
  );

  return [
    favorites as Exclude<typeof favorites, undefined>,
    setFavorites,
  ] as const;
};

export { getPricesView, path, QuickTrading };
